/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { ChartType } from "~/generated/graphql";
import {
  DateRange,
  EstatePeriod,
  Granularity,
  ChartType as CostAnalysisChartType,
} from "~/components";
import { useQueryState } from "~/hooks";
import {
  EstateGroupBy,
  EstateRecordsGroupingChart,
  EstateRecordsGroupingTable,
  getEndDate,
  getStartDate,
  useEstateRecordsCost,
  useEstateRecordsGroupByChartJob,
} from "~/views/estate/components/Charts";
import {
  REGION_SORT_OPTIONS,
  EstateChartsQueryState,
  TotalCostTable,
} from "./components";
import {
  isTableDataSortable,
  mapEstatePeriodToPeriod,
  sortDataByTotal,
} from "./helpers";

type Props = {
  period: EstatePeriod;
  granularity: Granularity;
  range: DateRange | undefined;
  setIsDataLoading: (loading: boolean) => void;
};

export function EstateRecordsRegionView({
  period,
  granularity,
  range,
  setIsDataLoading,
}: Props) {
  const {
    state: { periodStart, periodEnd },
  } = useQueryState<EstateChartsQueryState>();

  const startDate =
    periodStart ?? getStartDate(EstatePeriod.CURRENT_MONTH, undefined);
  const endDate =
    periodEnd ?? getEndDate(EstatePeriod.CURRENT_MONTH, undefined);

  const {
    chartData,
    isChartDataLoading,
    isTableDataLoading,
    isDataLoading,
    tableData,
    tableError,
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
  } = useEstateRecordsGroupByChartJob({
    chartType: ChartType.Region,
    startDate,
    endDate,
  });

  useEffect(() => {
    setIsDataLoading(isDataLoading);
  }, [isDataLoading, setIsDataLoading]);

  const mappedPeriod = mapEstatePeriodToPeriod(period);

  const {
    costPerGroupChartData,
    costPerGroupTableData,
    tableCount,
    extraDetails,
    allColumns,
    stackBarColors,
    chartKeys,
    firstColumnWidth,
  } = useEstateRecordsCost({
    chartData,
    tableData,
    granularity,
    period: mappedPeriod,
    startDate,
    endDate,
    chartType: CostAnalysisChartType.COST_PER_REGION,
  });

  const sortedCostPerRegionTableData = isTableDataSortable(sortField, period)
    ? sortDataByTotal(costPerGroupTableData, sortDirection)
    : costPerGroupTableData;

  return (
    <>
      <EstateRecordsGroupingChart
        data={costPerGroupChartData}
        granularity={granularity}
        loading={isChartDataLoading}
        startDate={startDate}
        endDate={endDate}
        period={period}
        colors={stackBarColors}
        keys={chartKeys}
        chartType={CostAnalysisChartType.COST_PER_REGION}
      />
      <TotalCostTable
        period={period}
        range={range}
        count={tableData?.chartJobTable?.data?.count ?? 0}
        isLoading={isTableDataLoading}
        chartType={EstateGroupBy.REGION}
        totalTop={extraDetails?.totalTop ?? "-"}
        totalOther={extraDetails?.totalOther ?? "-"}
      />
      <EstateRecordsGroupingTable
        loading={isTableDataLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={REGION_SORT_OPTIONS}
        tableCount={tableCount ?? 0}
        columns={allColumns}
        data={sortedCostPerRegionTableData}
        firstColumnWidth={firstColumnWidth}
        error={tableError}
        period={period}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortOrderChange}
      />
    </>
  );
}
